import React, { useState } from 'react';
import { X, Eye, Download, Share2, Printer, CheckCircle2, ShieldCheck, FileText } from 'lucide-react';
import { WorkOrder } from '../../types';
import { UserProfile } from '../../../../types';
import { formatCurrencyBRL } from '../../../../utils/formatUtils';
import { generateWorkOrderPdf } from '../../utils/workOrderPdfUtils';
import { formatDateFull, formatPhone, buildFullAddress } from '../../utils/receiptPdfUtils';

interface WorkOrderPdfPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrder: Partial<WorkOrder>;
  user?: Partial<UserProfile>;
}

export default function WorkOrderPdfPreviewModal({
  isOpen,
  onClose,
  workOrder,
  user
}: WorkOrderPdfPreviewModalProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  if (!isOpen) return null;

  const orderNum = workOrder.orderNumber || 'OS-001';
  const clientName = workOrder.clientName || 'Cliente';
  const clientPhone = workOrder.clientPhone ? formatPhone(workOrder.clientPhone) : '';
  const clientAddress = workOrder.clientAddress || '';
  const providerName = user?.businessName || user?.ownerName || 'Prestador Especializado';
  const providerAddress = buildFullAddress(user);
  const providerPhone = user?.phone ? formatPhone(user.phone) : '';
  const logoUrl = user?.logoUrl || user?.logoBase64;

  const items = workOrder.itemsBreakdown || [];
  const laborItems = items.filter(i => !i.isMaterial);
  const materialItems = items.filter(i => i.isMaterial);

  const checklists = workOrder.checklistData || [];
  const photos = workOrder.photos || [];
  const signatures = workOrder.signatures || [];

  const totalLabor = workOrder.totalLabor || laborItems.reduce((acc, curr) => acc + curr.total, 0);
  const totalMaterials = workOrder.totalMaterials || materialItems.reduce((acc, curr) => acc + curr.total, 0);
  const finalPrice = workOrder.finalPrice || (totalLabor + totalMaterials);

  const handleDownloadPdf = async () => {
    setIsGenerating(true);
    try {
      const result = await generateWorkOrderPdf(workOrder, user);
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 3000);
    } catch (err) {
      console.error('Erro ao gerar PDF da OS:', err);
      alert('Não foi possível gerar o PDF. Tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(
      `Olá, *${clientName}*! 👋 Segue o resumo da sua *Ordem de Serviço Nº ${orderNum}* ("${workOrder.title || 'Serviço'}").\n\n` +
      (workOrder.deviceData ? `📱 *Aparelho:* ${workOrder.deviceData.brand} ${workOrder.deviceData.model}\n` : '') +
      `💰 *Valor Total:* ${formatCurrencyBRL(finalPrice)}\n` +
      `📅 *Data:* ${formatDateFull(workOrder.createdAt)}\n` +
      (workOrder.warrantyTerms ? `🛡️ *Garantia:* Confira os prazos no documento impresso.\n` : '') +
      `\nFicamos à total disposição!`
    );
    if (workOrder.clientPhone) {
      const cleanPhone = workOrder.clientPhone.replace(/\D/g, '');
      const finalPhone = cleanPhone.startsWith('55') && cleanPhone.length > 11 ? cleanPhone : `55${cleanPhone}`;
      window.open(`https://api.whatsapp.com/send?phone=${finalPhone}&text=${text}`, '_blank');
    } else {
      window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/40 backdrop-blur-xs p-2 sm:p-4 overflow-y-auto font-sans">
      <div className="relative w-full max-w-4xl bg-white border border-zinc-200 rounded-3xl shadow-2xl flex flex-col my-auto max-h-[92vh] overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-200 bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center text-brand">
              <Eye className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-black text-zinc-900 uppercase tracking-wider">
                  Prévia da Ordem de Serviço ({orderNum})
                </h2>
                <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-black uppercase px-2 py-0.5 rounded-md flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> PDF Autêntico
                </span>
              </div>
              <p className="text-[11px] text-zinc-500">
                Visualização exata do laudo e termo de serviço antes do download ou envio.
              </p>
            </div>
          </div>
          
          <button 
            type="button"
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 rounded-full transition-colors cursor-pointer"
            title="Fechar Prévia"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content - Folha A4 Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-zinc-100/70">
          <div className="max-w-2xl mx-auto bg-white text-zinc-900 rounded-2xl shadow-xl p-6 sm:p-10 border border-zinc-200 space-y-6">
            
            {/* Header com Logo e Prestador */}
            <div className="flex justify-between items-start border-b-2 border-zinc-900 pb-5">
              <div className="flex items-center gap-3.5 max-w-[65%]">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="Logo" 
                    className="w-14 h-14 rounded-xl object-cover border border-zinc-200"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-emerald-50 border-2 border-brand flex items-center justify-center font-black text-brand text-sm">
                    OS
                  </div>
                )}
                <div>
                  <h3 className="text-base font-black text-zinc-950 uppercase tracking-tight">
                    {providerName}
                  </h3>
                  {providerAddress && (
                    <p className="text-[11px] text-zinc-500 font-medium leading-relaxed">{providerAddress}</p>
                  )}
                  {providerPhone && (
                    <p className="text-[11px] text-zinc-500 font-medium">WhatsApp: {providerPhone}</p>
                  )}
                </div>
              </div>

              <div className="text-right space-y-1">
                <span className="inline-block px-2.5 py-1 bg-brand text-white font-black text-[10px] uppercase tracking-wider rounded-lg">
                  Ordem de Serviço
                </span>
                <div className="text-base font-black text-zinc-900">{orderNum}</div>
                <div className="text-[10px] text-zinc-400 font-medium">{formatDateFull(workOrder.createdAt)}</div>
              </div>
            </div>

            {/* Dados do Cliente */}
            <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-4 space-y-1.5 text-xs">
              <span className="text-[9px] font-black text-zinc-400 uppercase tracking-wider block">
                Cliente & Local da Execução
              </span>
              <h4 className="text-sm font-black text-zinc-950">{clientName}</h4>
              {(clientPhone || clientAddress || workOrder.clientDocument) && (
                <p className="text-[11px] text-zinc-600 font-medium">
                  {[
                    workOrder.clientDocument ? `Doc: ${workOrder.clientDocument}` : null,
                    clientPhone ? `Tel: ${clientPhone}` : null,
                    clientAddress ? clientAddress : null
                  ].filter(Boolean).join(' • ')}
                </p>
              )}
              <div className="pt-2 border-t border-zinc-200 text-xs font-bold text-brand">
                Serviço: {workOrder.title || 'Prestação de Serviços Técnicos'}
              </div>
            </div>

            {/* Metadados Técnicos: Aparelho / Celular */}
            {workOrder.deviceData && (
              <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-4 space-y-1.5 text-xs">
                <span className="text-[9px] font-black text-zinc-400 uppercase tracking-wider block">
                  Aparelho / Dispositivo
                </span>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-zinc-500">Marca:</span> <span className="font-bold text-zinc-900">{workOrder.deviceData.brand || '-'}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Modelo:</span> <span className="font-bold text-zinc-900">{workOrder.deviceData.model || '-'}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">IMEI/Serial:</span> <span className="font-bold text-zinc-900">{workOrder.deviceData.imeiOrSerial || '-'}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Cor:</span> <span className="font-bold text-zinc-900">{workOrder.deviceData.color || '-'}</span>
                  </div>
                  {workOrder.deviceData.hasPasscode && (
                    <div className="col-span-2 mt-1">
                      <span className="text-zinc-500">Acesso de teste:</span> <span className="font-mono text-[10px] bg-zinc-200 px-1 rounded">Fornecido, valor não armazenado</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Itens do Escopo */}
            {items.length > 0 && (
              <div className="space-y-2">
                <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block">
                  Itens e Serviços Discriminados
                </span>
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-zinc-100 border-b border-zinc-200">
                      <th className="py-2 px-3 font-black text-zinc-600 uppercase">Item</th>
                      <th className="py-2 px-3 font-black text-zinc-600 uppercase text-center w-16">Qtd</th>
                      <th className="py-2 px-3 font-black text-zinc-600 uppercase text-right w-24">Unitário</th>
                      <th className="py-2 px-3 font-black text-zinc-600 uppercase text-right w-28">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="py-2.5 px-3 font-bold text-zinc-900">
                          {item.name}
                          <span className="text-[10px] text-zinc-400 font-normal ml-1">
                            ({item.isMaterial ? 'Insumo' : 'Mão de Obra'})
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center text-zinc-600">{item.quantity || 1}</td>
                        <td className="py-2.5 px-3 text-right text-zinc-600">{formatCurrencyBRL(item.unitPrice || 0)}</td>
                        <td className="py-2.5 px-3 text-right font-black text-zinc-900">{formatCurrencyBRL(item.total || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Resumo de Valores */}
            <div className="flex justify-end pt-2">
              <div className="w-64 bg-zinc-50 border border-zinc-200 rounded-xl p-3 space-y-1.5 text-xs">
                {totalMaterials > 0 && (
                  <div className="flex justify-between text-zinc-500">
                    <span>Materiais:</span>
                    <strong className="text-zinc-900">{formatCurrencyBRL(totalMaterials)}</strong>
                  </div>
                )}
                {totalLabor > 0 && (
                  <div className="flex justify-between text-zinc-500">
                    <span>Mão de Obra:</span>
                    <strong className="text-zinc-900">{formatCurrencyBRL(totalLabor)}</strong>
                  </div>
                )}
                <div className="flex justify-between border-t-2 border-zinc-900 pt-2 text-sm">
                  <span className="font-black text-zinc-900">Total da OS:</span>
                  <strong className="font-black text-brand">{formatCurrencyBRL(finalPrice)}</strong>
                </div>
              </div>
            </div>

            {/* Checklist Técnico */}
            {checklists.length > 0 && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 space-y-2">
                <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest block">
                  Checklist Técnico de Vistoria
                </span>
                <div className="grid grid-cols-2 gap-2 text-xs text-zinc-700">
                  {checklists.map((c, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <span className="text-brand font-black">✓</span>
                      <span>{typeof c === 'string' ? c : c.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fotos da Vistoria */}
            {photos.length > 0 && (
              <div className="space-y-2">
                <span className="text-[10px] font-black text-zinc-400 uppercase tracking-widest block">
                  Fotos de Vistoria ({photos.length})
                </span>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {photos.map((p, idx) => (
                    <div key={idx} className="border border-zinc-200 rounded-lg overflow-hidden bg-zinc-50">
                      <img src={p.photoUrl} alt={`Foto ${idx + 1}`} className="w-full h-16 object-cover" />
                      <span className="text-[8px] font-black uppercase text-zinc-500 block text-center py-0.5">
                        {p.photoType === 'before' ? 'Antes' : p.photoType === 'during' ? 'Durante' : 'Depois'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Garantia & Pagamento */}
            <div className="border-t border-zinc-200 pt-4 space-y-1 text-xs text-zinc-600">
              {workOrder.warrantyTerms && (
                <p><strong className="text-zinc-900">Garantia:</strong> {workOrder.warrantyTerms}</p>
              )}
              {workOrder.paymentTerms && (
                <p><strong className="text-zinc-900">Pagamento:</strong> {workOrder.paymentTerms}</p>
              )}
            </div>

            {/* Assinaturas */}
            {signatures.length > 0 ? (
              <div className="border-t-2 border-dashed border-zinc-300 pt-4 grid grid-cols-2 gap-4 text-center">
                {signatures.map((sig, idx) => (
                  <div key={idx} className="space-y-1">
                    <img src={sig.clientSignatureBase64 || sig.providerSignatureBase64 || ''} alt="Assinatura" className="h-10 mx-auto object-contain" />
                    <div className="border-t border-zinc-900 pt-1 text-xs font-black text-zinc-900">
                      {sig.clientName || sig.providerName || 'Assinante'} ({sig.clientSignatureBase64 ? 'Cliente' : 'Prestador'})
                    </div>
                    <div className="text-[8px] text-zinc-400 font-mono">
                      Hash: {sig.verificationHash?.slice(0, 16) || 'AUTÊNTICO'}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border-t border-zinc-200 pt-8 grid grid-cols-2 gap-6 text-center text-xs text-zinc-500">
                <div className="border-t border-zinc-400 pt-1">Assinatura do Prestador</div>
                <div className="border-t border-zinc-400 pt-1">Assinatura do Cliente</div>
              </div>
            )}

          </div>
        </div>

        {/* Modal Footer Actions */}
        <div className="p-4 sm:p-5 border-t border-zinc-200 bg-zinc-50 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-zinc-600 text-center sm:text-left">
            {downloadSuccess ? (
              <span className="text-emerald-700 font-bold flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4" /> PDF baixado com sucesso no seu dispositivo!
              </span>
            ) : (
              <span>Documento pronto para exportação em PDF de alta qualidade.</span>
            )}
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <button
              type="button"
              onClick={handleShareWhatsApp}
              className="flex-1 sm:flex-none py-3 px-4 bg-emerald-700 hover:bg-emerald-600 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-2 transition-all cursor-pointer"
              title="Compartilhar no WhatsApp"
            >
              <Share2 className="h-4 w-4" />
              <span>Enviar no WhatsApp</span>
            </button>

            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={isGenerating}
              className="flex-1 sm:flex-none py-3 px-5 bg-brand hover:bg-brand-dark text-white font-black rounded-xl text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-lg shadow-brand/20 cursor-pointer active:scale-95 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              <span>{isGenerating ? 'Gerando PDF Real...' : 'Baixar PDF da OS'}</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
